import {useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Link, useNavigate, useParams} from "react-router-dom";
import {Building2, CheckCircle2, FileText, GitBranch, Image, KeyRound, Landmark, Pencil, Power, Users} from "lucide-react";
import {mapApiError} from "../../shared/api/errors";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {ADMIN_ROLES} from "../../shared/auth/roles";
import {ActionChecklist, MediaLinks, displayValue, yesNo} from "../../shared/components/BusinessBlocks";
import {ConfirmationDialog} from "../../shared/components/ConfirmationDialog";
import {DataState} from "../../shared/components/DataState";
import {DetailGrid} from "../../shared/components/DetailGrid";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {WorkflowRail} from "../../shared/components/WorkflowRail";
import {useTranslation} from "../../shared/i18n";
import {formatMoney, formatNumber} from "../../shared/utils/format";
import {deletePlace, loadPlace, rotatePlaceCredential} from "./api";
import {CredentialRotationDialog} from "./components/CredentialRotationDialog";
import {PartnerBotSection} from "./components/PartnerBotSection";
import "./organizations.css";

const MANAGER_ROLES = new Set([
    ADMIN_ROLES.SUPERADMIN,
    ADMIN_ROLES.ADMINISTRATOR,
    ADMIN_ROLES.DEPLOYER,
    ADMIN_ROLES.IMPLEMENTATION_HEAD,
]);

export const OrganizationDetailPage = () => {
    const {t} = useTranslation();
    const {employer_id: placeId} = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const capabilities = useCapabilities();
    const [dialog, setDialog] = useState(null);
    const [dialogError, setDialogError] = useState(null);
    const [mutationResult, setMutationResult] = useState(null);
    const placeQuery = useQuery({
        queryKey: ["admin-place", placeId],
        queryFn: () => loadPlace(placeId),
        enabled: Boolean(placeId),
    });

    const invalidateOrganizations = () => {
        void queryClient.invalidateQueries({queryKey: ["admin-places"]});
        void queryClient.invalidateQueries({queryKey: ["admin-place"]});
    };

    const deleteMutation = useMutation({
        mutationFn: () => deletePlace(placeId),
        onSuccess: () => {
            invalidateOrganizations();
            setDialog(null);
            navigate("/employers");
        },
        onError: (error) => setDialogError(mapApiError(error)),
    });

    const credentialMutation = useMutation({
        mutationFn: (password) => rotatePlaceCredential(placeId, password),
        onSuccess: (response) => {
            invalidateOrganizations();
            setDialog(null);
            setDialogError(null);
            setMutationResult({kind: "success", message: response?.message || t("orgs.common.credentialsUpdated")});
        },
        onError: (error) => setDialogError(mapApiError(error)),
    });

    const place = placeQuery.data || {};
    const branches = place.branches || place.branch || place.branch_set || [];
    const mediaItems = [
        {label: t("orgs.placeForm.logo"), url: place.logo},
        {label: t("orgs.placeForm.background"), url: place.background_image},
        {label: t("orgs.placeForm.agreement"), url: place.agreement, type: "document"},
    ];
    const readinessItems = [
        {label: t("orgs.detail.profile"), ok: Boolean(place.name && place.head_name && place.phone_number)},
        {label: t("orgs.detail.requisites"), ok: Boolean(place.bin_number && place.account_number && place.agreement)},
        {label: t("orgs.detail.media"), ok: Boolean(place.logo || !place.logo_exists) && Boolean(place.background_image)},
        {label: t("orgs.common.branches"), ok: branches.length > 0},
        {label: t("orgs.detail.showcase"), ok: Boolean(place.is_shown)},
        {label: t("orgs.detail.owner"), ok: Boolean(place.created_by)},
    ];
    const readinessDone = readinessItems.filter((item) => item.ok).length;
    const readinessPercent = Math.round((readinessDone / readinessItems.length) * 100);
    const visibleBranches = branches.filter((branch) => branch.is_shown).length;
    const hiddenBranches = Math.max(0, branches.length - visibleBranches);
    const canManage = placeQuery.isSuccess && Boolean(place.name)
        && capabilities.hasAction("organization.manage")
        && MANAGER_ROLES.has(capabilities.user?.role);
    const cityName = place.city_name || place.city;
    const categoryName = place.category_name || place.category;

    const openDialog = (nextDialog) => {
        setMutationResult(null);
        setDialogError(null);
        setDialog(nextDialog);
    };

    const closeDialog = () => {
        if (deleteMutation.isPending || credentialMutation.isPending) return;
        setDialog(null);
        setDialogError(null);
    };

    return (
        <div className="admin-page">
            <header className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">{place.name || t("orgs.common.placeNumber", {id: placeId})}</h1>
                    <p className="admin-page-copy">
                        {t("orgs.detail.copy")}
                    </p>
                </div>
                <div className="organization-header-actions">
                    {canManage ? (
                        <>
                            <Link className="admin-button admin-button-secondary" to={`/employers/${placeId}/edit`}>
                                <Pencil aria-hidden="true" size={15} /> {t("orgs.common.edit")}
                            </Link>
                            <button className="admin-button admin-button-secondary" type="button" onClick={() => openDialog("credential")}>
                                <KeyRound aria-hidden="true" size={15} /> {t("orgs.detail.changePassword")}
                            </button>
                            <button className="admin-button organization-danger-button" type="button" onClick={() => openDialog("delete")}>
                                <Power aria-hidden="true" size={15} /> {t("orgs.detail.disable")}
                            </button>
                        </>
                    ) : null}
                    <Link className="admin-button admin-button-primary" to={`/employers/${placeId}/branch/add`}>
                        <GitBranch aria-hidden="true" size={15} /> {t("orgs.common.newBranch")}
                    </Link>
                </div>
            </header>

            <MutationResultBanner result={mutationResult} />

            {placeQuery.isLoading ? (
                <DataState title={t("orgs.placeEditor.loadingTitle")} copy={t("orgs.detail.loadingCopy")} />
            ) : placeQuery.isError ? (
                <DataState title={t("orgs.placeEditor.errorTitle")} copy={t("orgs.detail.errorCopy")} />
            ) : (
                <div className="admin-workflow">
                    <WorkflowRail
                        steps={[
                            {title: t("orgs.detail.profile"), copy: t("orgs.detail.stepProfile")},
                            {title: t("orgs.common.branches"), copy: t("orgs.detail.stepBranches")},
                            {title: t("orgs.detail.stepMoney"), copy: t("orgs.detail.stepMoneyCopy")},
                            {title: t("orgs.detail.stepContent"), copy: t("orgs.detail.stepContentCopy")},
                        ]}
                    />
                    <section className="admin-page">
                        <section className="admin-grid cols-4">
                            <article className="admin-card">
                                <p className="admin-card-title">{t("orgs.detail.branchCount")}</p>
                                <p className="admin-card-value">{formatNumber(branches.length || place.branch_count)}</p>
                                <p className="admin-card-note">{t("orgs.detail.branchVisibility", {visible: formatNumber(visibleBranches), hidden: formatNumber(hiddenBranches)})}</p>
                            </article>
                            <article className="admin-card">
                                <p className="admin-card-title">{t("orgs.common.category")}</p>
                                <p className="admin-card-value">{categoryName || "—"}</p>
                            </article>
                            <article className="admin-card">
                                <p className="admin-card-title">{t("orgs.common.city")}</p>
                                <p className="admin-card-value">{cityName || "—"}</p>
                            </article>
                            <article className="admin-card">
                                <p className="admin-card-title">{t("orgs.common.commission")}</p>
                                <p className="admin-card-value">{place.off_percent || "—"}</p>
                            </article>
                        </section>

                        <section className="admin-grid cols-2">
                            <article className="admin-card admin-health-card">
                                <p className="admin-card-title"><CheckCircle2 size={14} /> {t("orgs.detail.readiness")}</p>
                                <p className="admin-card-value">{readinessPercent}%</p>
                                <div className="admin-progress-track">
                                    <span style={{width: `${readinessPercent}%`}} />
                                </div>
                                <div className="admin-readiness-list">
                                    {readinessItems.map((item) => (
                                        <span className={item.ok ? "is-ready" : ""} key={item.label}>
                                            {item.label}
                                        </span>
                                    ))}
                                </div>
                            </article>
                            <article className="admin-card">
                                <p className="admin-card-title"><Landmark size={14} /> {t("orgs.detail.nextChecks")}</p>
                                <ActionChecklist
                                    items={[
                                        t("orgs.detail.checkLegal"),
                                        t("orgs.detail.checkBranch"),
                                        t("orgs.detail.checkMedia"),
                                    ]}
                                />
                            </article>
                        </section>

                        <article className="admin-card">
                            <p className="admin-card-title">{t("orgs.detail.main")}</p>
                            <DetailGrid
                                items={[
                                    {label: t("orgs.detail.head"), value: displayValue(place.head_name || place.legal_name || place.admin_name)},
                                    {label: t("orgs.common.phone"), value: displayValue(place.phone_number)},
                                    {label: t("orgs.common.city"), value: displayValue(cityName)},
                                    {label: t("orgs.common.category"), value: displayValue(categoryName)},
                                    {label: t("orgs.detail.deployer"), value: displayValue(place.created_by)},
                                    {label: t("orgs.detail.shownInApp"), value: yesNo(place.is_shown)},
                                ]}
                            />
                        </article>

                        <section className="admin-grid cols-2">
                            <article className="admin-card">
                                <p className="admin-card-title"><Landmark size={14} /> {t("orgs.detail.legalTitle")}</p>
                                <DetailGrid
                                    items={[
                                        {label: t("orgs.detail.bin"), value: displayValue(place.bin_number)},
                                        {label: t("orgs.common.account"), value: displayValue(place.account_number)},
                                        {label: t("orgs.common.commission"), value: displayValue(place.off_percent)},
                                        {label: t("orgs.detail.bonusOnly"), value: yesNo(place.bonus_only)},
                                    ]}
                                />
                            </article>
                            <article className="admin-card">
                                <p className="admin-card-title"><KeyRound size={14} /> {t("orgs.detail.accessTitle")}</p>
                                <DetailGrid
                                    items={[
                                        {label: t("orgs.common.login"), value: displayValue(place.login)},
                                        {label: t("orgs.common.password"), value: t("orgs.detail.passwordHidden")},
                                        {label: t("orgs.placeForm.logo"), value: place.logo_exists ? t("orgs.detail.logoUploaded") : t("orgs.detail.logoMissing")},
                                        {label: t("orgs.detail.media"), value: mediaItems.filter((item) => item.url).length},
                                    ]}
                                />
                            </article>
                        </section>

                        <article className="admin-card">
                            <p className="admin-card-title">{t("orgs.common.branches")}</p>
                            <p className="admin-card-note">{t("orgs.detail.branchesNote")}</p>
                            <div className="admin-mini-grid">
                                {branches.length ? (
                                    branches.map((branch) => (
                                        <Link
                                            className="admin-mini-card admin-branch-card"
                                            key={branch.id}
                                            to={`/employers/${placeId}/branch/${branch.id}`}
                                        >
                                            <div className="admin-mini-card-icon"><Building2 size={16} /></div>
                                            <div>
                                                <strong>{branch.name || t("orgs.common.branchNumber", {id: branch.id})}</strong>
                                                <span>{displayValue(branch.city)}</span>
                                                <small>{displayValue(branch.off_percent)} · {formatMoney(branch.branch_sum)}</small>
                                                <StatusBadge tone={branch.is_shown ? "success" : "warning"}>
                                                    {branch.is_shown ? t("orgs.detail.inApp") : t("orgs.detail.hidden")}
                                                </StatusBadge>
                                            </div>
                                        </Link>
                                    ))
                                ) : (
                                    <DataState title={t("orgs.detail.noBranchesTitle")} copy={t("orgs.detail.noBranchesCopy")} />
                                )}
                            </div>
                        </article>

                        <article className="admin-card">
                            <p className="admin-card-title"><FileText size={14} /> {t("orgs.detail.mediaTitle")}</p>
                            <p className="admin-card-note">
                                <Users size={13} /> <Image size={13} /> {t("orgs.detail.mediaNote")}
                            </p>
                            <MediaLinks items={mediaItems} />
                        </article>

                        <PartnerBotSection placeId={placeId} entity={place.name || t("orgs.common.placeNumber", {id: placeId})} />
                    </section>
                </div>
            )}

            <ConfirmationDialog
                open={dialog === "delete"}
                title={t("orgs.detail.disableTitle")}
                description={t("orgs.detail.disableCopy")}
                entity={place.name}
                confirmationText={place.name}
                confirmLabel={t("orgs.detail.disableConfirm")}
                tone="destructive"
                pending={deleteMutation.isPending}
                error={dialogError}
                dialogKey={`delete-place-${placeId}`}
                onCancel={closeDialog}
                onConfirm={() => deleteMutation.mutate()}
            />
            <CredentialRotationDialog
                open={dialog === "credential"}
                entity={place.name}
                pending={credentialMutation.isPending}
                error={dialogError}
                onCancel={closeDialog}
                onConfirm={(password) => credentialMutation.mutate(password)}
            />
        </div>
    );
};
